import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DALARNA_CITIES, type CityOption } from "@/hooks/useExploreLocation";

interface ExploreLocationPromptProps {
  /** Whether geolocation was denied by the user */
  denied: boolean;
  /** Loading state for geo request */
  loading: boolean;
  /** Trigger browser geolocation */
  onRequestGeo: () => void;
  /** Select a city fallback */
  onSelectCity: (city: CityOption) => void;
}

/**
 * Mobile: banner under search field.
 * Desktop: compact inline prompt.
 *
 * Shows geo prompt by default, city picker when geo denied.
 */
export function ExploreLocationPrompt({
  denied,
  loading,
  onRequestGeo,
  onSelectCity,
}: ExploreLocationPromptProps) {
  if (denied) {
    return (
      <div className="px-4 py-3 bg-muted/40 border-b border-border">
        <p className="text-sm text-muted-foreground mb-2">
          Vi kunde inte hämta din position. Du kan välja stad istället.
        </p>
        <div className="flex flex-wrap gap-2">
          {DALARNA_CITIES.map((city) => (
            <Button
              key={city.name}
              variant="outline"
              size="sm"
              className="h-8 text-xs rounded-full"
              onClick={() => onSelectCity(city)}
            >
              {city.name}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 bg-muted/40 border-b border-border">
      <div className="flex items-center gap-3">
        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
        <p className="text-sm text-muted-foreground flex-1">
          Vill du se det som finns nära dig?
        </p>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 h-8 text-xs rounded-full gap-1.5"
          onClick={onRequestGeo}
          disabled={loading}
        >
          {loading ? "Söker…" : "Visa nära mig"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Desktop variant — discrete text link for the header area.
 */
export function ExploreLocationLink({
  denied,
  loading,
  onRequestGeo,
  onSelectCity,
}: ExploreLocationPromptProps) {
  if (denied) {
    return (
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span>Kunde inte hämta position.</span>
        <span className="text-muted-foreground/60">Välj stad:</span>
        {DALARNA_CITIES.slice(0, 4).map((city) => (
          <button
            key={city.name}
            onClick={() => onSelectCity(city)}
            className="text-primary hover:underline text-sm"
          >
            {city.name}
          </button>
        ))}
      </div>
    );
  }

  return (
    <button
      onClick={onRequestGeo}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
    >
      <MapPin className="h-3.5 w-3.5" />
      {loading ? "Söker position…" : "Visa det som finns nära dig"}
    </button>
  );
}
